import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrayHelpers, FieldArray, FormikProps } from 'formik';
import { Accordion, Card, Form, InputGroup } from 'react-bootstrap';
import { CustomButton, FormTextField } from '../../../../../components';
import { AddIcon } from '../../../../../assets/icons/AddIcon';
import { PlayValues } from '../PlayTag/PlayTag';
import { CustomAccordionHeader } from '../../../common/components/CustomAccordionHeader';
import { openModal } from '../../../../../features/modal/modalSlice';
import { ModalAction } from '../../../../../types/modal';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { ClassTeam } from '../../../TournamentClasses/components/ClassesTag/ClassesTag';
import uniqId from 'uniqid';
import { CloseCircle } from '../../../../../assets/icons/CloseCircle';
import { TeamPlayCard } from '../TeamPlayCard';
import { Group } from '../../../../../types/tournament';
import { GroupIcon } from '../../../../../assets/icons/GroupIcon';
import { selectClass } from '../../../../../features/class/classSelectors';
import { generateGroups } from '../../../../../features/tournament/actionCreators';
import { ClassTypeEnum } from '../../../../../types/enums';
import {
  canAddTeamPlayGroups,
  shouldShowTeamPlayGroupsSection,
} from '../../../../../utils/teamPlayActiveCategories.util';
import { recordGroupMatchesRemoval } from '../../../../../utils/bracketCellPendingDeletes.util';
import { getDisplayName } from '../../../../../utils/userDisplayName';

interface GroupSectionProps {
  formikArg: FormikProps<PlayValues>;
  teams: ClassTeam[];
  currentClass: null | unknown | any;
  isBoxLeague: boolean | null;
  onPlayDataRefresh?: () => Promise<void>;
}

export const GroupSection: FC<GroupSectionProps> = (props) => {
  const { t } = useTranslation();
  const { teams, formikArg, currentClass, isBoxLeague, onPlayDataRefresh } = props;

  const groups = formikArg.values.tournamentGroups;
  const type = formikArg.initialValues.type;
  const dispatch = useAppDispatch();
  const { currentClassId } = useAppSelector(selectClass);

  const prefix = 'creativeDetail-';
  const inputPrefix = 'input-';
  const buttonPrefix = 'button-';

  const hasExistingGroups = groups.length > 0;
  const isTeamsClass = type === ClassTypeEnum.TEAMS;
  const showGroupsSection = shouldShowTeamPlayGroupsSection(
    isTeamsClass,
    currentClass?.tournamentCategories,
    groups.length,
  );
  const canAddGroups = canAddTeamPlayGroups(
    isTeamsClass,
    currentClass?.tournamentCategories,
  );

  const handleOpenModal = useCallback(
    (group: Group, boxMode: boolean) => {
      dispatch(
        openModal({
          currentModal: ModalAction.ADD_GROUPS_PLAY,
          modalOpen: true,
          data: {
            currentGroup: group,
            registeredTeams: teams,
            setGroups: formikArg.setFieldValue,
            groups,
            mode: boxMode,
          },
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, teams]
  );

  return (
    <FieldArray name="tournamentGroups">
      {({ push, remove }: ArrayHelpers) =>
        teams.length ? (
          <>
            {showGroupsSection ? (
              <>
                <div className="my-3 d-flex justify-content-between align-items-center">
                  {!isBoxLeague ? (
                    <h4 className="font-weight-bold">{t(prefix + 'groups')}</h4>
                  ) : (
                    <h4 className="font-weight-bold">{t(prefix + 'boxes')}</h4>
                  )}
                  {canAddGroups ? (
                    <div className="d-flex align-items-center">
                      {(!groups.length && !hasExistingGroups && (
                        <CustomButton
                          variant="outline-dark"
                          btnText={t(buttonPrefix + 'generate')}
                          cb={async () => {
                            await dispatch(generateGroups(currentClassId as number));
                            await onPlayDataRefresh?.();
                          }}
                          className="d-flex justify-content-between align-items-center me-1"
                        >
                          <AddIcon />
                        </CustomButton>
                      )) ||
                        null}
                      <CustomButton
                        variant="outline-dark"
                        btnText={t(buttonPrefix + (isBoxLeague ? 'addBox' : 'addGroup'))}
                        cb={() => {
                          push({
                            id: uniqId(),
                            name: `${isBoxLeague ? 'Box' : 'Group'} name ${groups.length + 1}`,
                            teams: [],
                          });
                        }}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <AddIcon />
                      </CustomButton>
                    </div>
                  ) : null}
                </div>
                {groups.length
                  ? groups.map((group, index) => {
                      return (
                        <div key={group.id} className="container-fluid px-0 my-3">
                          <Accordion>
                            <Card>
                              <Card.Header className="p-0 bg-white border-0">
                                <CustomAccordionHeader eventKey={index.toString()}>
                                  <InputGroup>
                                    <Form.Text className="mt-0">
                                      <div className="d-flex p-2 h-100 flex-column justify-content-center">
                                        <GroupIcon h="25" w="25" />
                                      </div>
                                    </Form.Text>
                                    <FormTextField
                                      className="py-0 border-1 w-75 border-white"
                                      name={`tournamentGroups.${index}.name`}
                                      index={index}
                                      placeholder={t(inputPrefix + 'placeholderGroupName')}
                                    />
                                  </InputGroup>
                                </CustomAccordionHeader>
                              </Card.Header>
                              <Accordion.Collapse eventKey={index.toString()}>
                                <Card.Body className="p-2">
                                  <div className="d-flex flex-wrap flex-column flex-lg-row">
                                    <div className="col-lg-4 d-flex flex-column flex-lg-row align-items-end align-items-lg-start px-0 px-lg-2">
                                      {!isBoxLeague ? (
                                        <CustomButton
                                          className="d-flex justify-content-evenly align-items-center"
                                          variant="outline-dark"
                                          btnText={t(buttonPrefix + 'addTeams')}
                                          cb={() => handleOpenModal(group, false)}
                                        >
                                          <AddIcon />
                                        </CustomButton>
                                      ) : (
                                        <CustomButton
                                          className="d-flex justify-content-evenly align-items-center"
                                          variant="outline-dark"
                                          btnText={t(buttonPrefix + 'addPlayers')}
                                          cb={() => handleOpenModal(group, true)}
                                        >
                                          <AddIcon />
                                        </CustomButton>
                                      )}
                                    </div>

                                    {!isBoxLeague ? (
                                      <FieldArray name={`tournamentGroups.${index}.teams`}>
                                        {({ remove }: ArrayHelpers) => (
                                          <div className="col px-0">
                                            <div className="d-flex flex-column py-3 py-lg-0">
                                              <p className="mb-0 p-1">{t(prefix + 'teams')}</p>
                                              <div className="d-flex flex-wrap">
                                                {group.teams.length ? (
                                                  group.teams.map((team, i) =>
                                                    team === null ? null : (
                                                      <React.Fragment key={team.id}>
                                                        <TeamPlayCard
                                                          onClick={() => {
                                                            remove(i);
                                                          }}
                                                          team={team}
                                                        />
                                                      </React.Fragment>
                                                    )
                                                  )
                                                ) : (
                                                  <p className="fw-lighter ps-1">
                                                    {t(prefix + 'chooseTeam')}
                                                  </p>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </FieldArray>
                                    ) : (
                                      <FieldArray name={`tournamentGroups.${index}.teams`}>
                                        {() => {
                                          const uniqueUsersMap = new Map<number, any>();

                                          group.teams.forEach((team) => {
                                            if (!team || !(team as any).users) return;
                                            (team as any).users?.forEach((user: any) => {
                                              if (!user) return;
                                              let userId: number;
                                              let userObj: any;

                                              if (typeof user === 'object' && user !== null) {
                                                userId = user.id;
                                                userObj = user;
                                              } else {
                                                userId = user;

                                                // Check user in teams prop
                                                const foundTeam = teams.find((t) =>
                                                  t.users?.some((u: any) => (u.id || u) === userId)
                                                );

                                                if (foundTeam) {
                                                  userObj = foundTeam.users?.find(
                                                    (u: any) => (u.id || u) === userId
                                                  );
                                                } else {
                                                  // Fallback if not found
                                                  userObj = {
                                                    id: userId,
                                                    name: 'Unknown',
                                                    avatar: null,
                                                  };
                                                }
                                              }

                                              if (userId && !uniqueUsersMap.has(userId)) {
                                                uniqueUsersMap.set(userId, userObj);
                                              }
                                            });
                                          });

                                          const uniqueUsers = Array.from(
                                            uniqueUsersMap.values()
                                          ).filter((u) => u && u.id);

                                          return (
                                            <div className="col px-0">
                                              <div className="d-flex flex-column py-3 py-lg-0">
                                                <p className="mb-0 p-1">{t(prefix + 'players')}</p>{' '}
                                                <div className="d-flex flex-wrap">
                                                  {uniqueUsers.length ? (
                                                    uniqueUsers.map((user) => (
                                                      <React.Fragment key={user.id}>
                                                        <TeamPlayCard
                                                          team={{
                                                            id: user.id,
                                                            name: getDisplayName(user) || user.name,
                                                            logo: user.avatar,
                                                            seed: teams.find(
                                                              (t) =>
                                                                t.users &&
                                                                t.users.length === 1 &&
                                                                t.users[0]?.id === user.id
                                                            )?.seed, // Players may not have seed
                                                          }}
                                                          onClick={() => {
                                                            const userIdToRemove = user.id;

                                                            const remainingTeams =
                                                              group.teams.filter((t) => {
                                                                // Keep team only if the user is not in it
                                                                const userIds = (
                                                                  t as any
                                                                ).users.map((u: any) =>
                                                                  typeof u === 'number' ? u : u.id
                                                                );
                                                                return !userIds.includes(
                                                                  userIdToRemove
                                                                );
                                                              });

                                                            formikArg.setFieldValue(
                                                              `tournamentGroups.${index}.teams`,
                                                              remainingTeams
                                                            );
                                                          }}
                                                        />
                                                      </React.Fragment>
                                                    ))
                                                  ) : (
                                                    <p className="fw-lighter ps-1">
                                                      {t(prefix + 'choosePlayers')}
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        }}
                                      </FieldArray>
                                    )}
                                  </div>
                                  <div className="d-flex justify-content-start justify-content-lg-end">
                                    <CustomButton
                                      className="d-flex align-items-center"
                                      btnText={t(buttonPrefix + 'removeGroup')}
                                      variant="outline-dark"
                                      onClick={() => {
                                        if (typeof group.id === 'number') {
                                          recordGroupMatchesRemoval(formikArg, group.id);
                                        }
                                        remove(index);
                                      }}
                                    >
                                      <CloseCircle />
                                    </CustomButton>
                                  </div>
                                </Card.Body>
                              </Accordion.Collapse>
                            </Card>
                          </Accordion>
                        </div>
                      );
                    })
                  : null}
              </>
            ) : (
              <div className="d-flex flex-column align-items-end fw-lighter">
                <p>{t(prefix + 'warning8')}</p>
              </div>
            )}
          </>
        ) : (
          <div className="d-flex justify-content-end fw-lighter">
            <p>{t(prefix + 'warning3')}</p>
          </div>
        )
      }
    </FieldArray>
  );
};
